import { motion } from 'framer-motion';
import { Euro, FileText, HelpCircle, Users } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { PROBLEM } from '@/constants/testIds';
import { useI18n } from '@/i18n/I18nProvider';
import { BRAND } from '@/lib/brandAssets';

const BARRIER_ICONS = [Euro, FileText, HelpCircle];

export default function Problem() {
  const { m } = useI18n();
  const p = m.problem;
  return (
    <section id="problem" data-testid={PROBLEM.root} className="relative section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-[1.05fr_0.95fr] items-start">
          {/* LEFT: copy + barriers */}
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
              {p.eyebrow}
            </div>
            <h2 className="font-display mt-3 text-3xl sm:text-4xl lg:text-[44px] tracking-[-0.015em] leading-[1.05]">
              {p.heading}<span className="text-primary">{p.headingEm}</span>
            </h2>
            <p className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-xl">
              {p.lead}
            </p>

            <div className="mt-8">
              <div className="text-[10px] uppercase tracking-[0.18em] text-primary/80 font-medium">
                {p.barriersTitle}
              </div>
              <div className="mt-3 grid sm:grid-cols-3 gap-3">
                {p.barriers.map((b, i) => {
                  const Icon = BARRIER_ICONS[i] || Euro;
                  return (
                    <motion.div
                      key={b.tag}
                      initial={{ opacity: 0, y: 12 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: '-60px' }}
                      transition={{ duration: 0.5, delay: i * 0.08 }}
                    >
                      <Card className="bg-card border border-border rounded-xl p-4 card-hover hover:border-primary/40 h-full">
                        <div className="h-9 w-9 rounded-lg border border-border bg-secondary flex items-center justify-center text-primary">
                          <Icon className="h-4 w-4" />
                        </div>
                        <div className="mt-3 font-display text-base leading-tight">{b.tag}</div>
                        <p className="mt-1.5 text-xs text-muted-foreground leading-relaxed">{b.body}</p>
                      </Card>
                    </motion.div>
                  );
                })}
              </div>
            </div>

            <div className="mt-8 rounded-2xl border border-border bg-secondary/30 p-5">
              <div className="flex items-start gap-3">
                <div className="h-9 w-9 shrink-0 rounded-lg border border-border bg-background flex items-center justify-center text-primary">
                  <Users className="h-4 w-4" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                    {p.consumersTitle}
                  </div>
                  <p className="mt-1 text-sm leading-relaxed text-foreground/90 max-w-xl">
                    {p.consumers}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT: visual — the two-bottle comparison from the deck (no overlay text — the slide already labels both bottles) */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <div className="relative aspect-[4/5] rounded-2xl overflow-hidden border border-border glow-ring brand-img-wrap bg-secondary/20">
              <img
                src={BRAND.problem}
                alt="Real impact vs greenwashing — verified by Klymart"
                className="brand-img-right"
                draggable={false}
              />
              {/* Soft vignette — strong mask at bottom hides any baked slide branding */}
              <div
                aria-hidden
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    'linear-gradient(180deg, rgba(8,16,12,0.18) 0%, rgba(8,16,12,0) 30%, rgba(8,16,12,0.55) 60%, rgba(8,16,12,0.95) 78%, rgb(8,16,12) 86%, rgb(8,16,12) 100%)',
                }}
              />
            </div>
            {/* Result panel — below the image */}
            <div className="mt-3 grid grid-cols-2 gap-2">
              <div className="rounded-xl border border-accent/30 bg-accent/10 px-3 py-2.5">
                <div className="text-[9px] uppercase tracking-wider text-accent">{p.result}</div>
                <div className="text-xs text-foreground/90 leading-snug mt-0.5">{p.resultA}</div>
              </div>
              <div className="rounded-xl border border-border bg-secondary/40 px-3 py-2.5">
                <div className="text-[9px] uppercase tracking-wider text-muted-foreground">&nbsp;</div>
                <div className="text-xs text-foreground/90 leading-snug mt-0.5">{p.resultB}</div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
